import { usePromptSadminUploads, usePromptUploads } from '@/features/voice-campaigns/api';
import type { TtsConvertRequest, TtsConvertResponse } from '@/features/voice-library/types';
import { api } from '@/lib/api/client';
import { useAuthStore } from '@/lib/auth/store';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { variableSampleText } from '../lib/variable-samples';
import type { TtsRow } from '../types';

export type StepMode = 'prompt' | 'text' | 'variable';

export interface ResolvedStep {
  index: number;
  mode: StepMode;
  label: string;
  detail?: string;
  url?: string;
  playable: boolean;
}

interface Args {
  /** Rows this instance resolves — the pane passes the whole flow,
   *  a step card passes just its own row. */
  rows: TtsRow[];
  language?: string;
  gender?: string;
  /** Flow owner's id — admin previews synthesize against the owner,
   *  not the viewer. */
  ownerUserId?: number;
  /** Cache a synthesized preview onto the row (text + variable rows). */
  onPatchRow: (index: number, patch: Partial<TtsRow>) => void;
}

/** Shared TTS preview playback — step resolution, single-source audio,
 *  on-demand synthesis and the play-all loop. Used by both the preview
 *  pane and TtsStepCard so they behave identically. */
export function useTtsPreview({ rows, language, gender, ownerUserId, onPatchRow }: Args) {
  const viewerId = useAuthStore((s) => s.userId);
  const sadmin = useAuthStore((s) => s.isSubOrSuperAdmin());
  const sadminPrompts = usePromptSadminUploads({ includeTtsFlow: true, enabled: sadmin });
  const userPrompts = usePromptUploads({ includeTtsFlow: true, enabled: !sadmin });
  const prompts = sadmin ? sadminPrompts : userPrompts;
  const userId = ownerUserId ?? viewerId;

  /** Resolve each row into what the listener will actually hear. */
  const steps: ResolvedStep[] = useMemo(() => {
    return rows.map((row, i): ResolvedStep => {
      if (row.ttsType === 'variable') {
        // Variables hold runtime data — the preview reads a static
        // per-type sample so each type sounds different.
        const sample = variableSampleText(row.variableType);
        return {
          index: i,
          mode: 'variable',
          label: row.value ? `Slot ${row.value}` : 'Variable (no slot)',
          detail: row.variableType ? `Reads as "${sample}"` : 'Pick a type',
          url: row.previewUrl,
          playable: Boolean(row.variableType),
        };
      }
      if (typeof row.text === 'string') {
        return {
          index: i,
          mode: 'text',
          label: row.text || '(no text yet)',
          detail: row.previewUrl ? 'Preview cached' : 'Synthesized on play',
          url: row.previewUrl,
          playable: Boolean(row.text && row.text.trim().length > 0),
        };
      }
      const match = (prompts.data ?? []).find((p) => String(p.promptId) === row.value);
      const url = (match as { promptUrl?: string } | undefined)?.promptUrl;
      return {
        index: i,
        mode: 'prompt',
        label: match?.fileName ?? match?.promptName ?? `#${row.value || '?'} is not present`,
        detail: match ? undefined : row.value ? undefined : 'Pick a prompt',
        url,
        playable: Boolean(url),
      };
    });
  }, [rows, prompts.data]);

  /* ---- Single-source playback coordinator ----
   * `runIdRef` tags each playback run: a superseded run (stopped or
   * replaced) can never touch the new run's state. `busyRef` /
   * `playingIdxRef` mirror the render state so click handlers decide
   * from fresh values even before React re-renders. */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const resolveRef = useRef<(() => void) | null>(null);
  const runIdRef = useRef(0);
  const busyRef = useRef<'playAll' | 'single' | null>(null);
  const playingIdxRef = useRef<number | null>(null);
  const [playingIndex, setPlayingIndex] = useState<number | null>(null);
  const [busy, setBusy] = useState<'playAll' | 'single' | null>(null);
  const [synthesizingIndex, setSynthesizingIndex] = useState<number | null>(null);

  const showPlaying = (i: number | null) => {
    playingIdxRef.current = i;
    setPlayingIndex(i);
  };
  const showBusy = (b: 'playAll' | 'single' | null) => {
    busyRef.current = b;
    setBusy(b);
  };

  const stopAll = () => {
    // Bump the run id FIRST — any in-flight loop bails on its next check.
    runIdRef.current += 1;
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    resolveRef.current?.();
    resolveRef.current = null;
    showPlaying(null);
    showBusy(null);
  };

  // Unmount (modal close, row removed) — HTML audio keeps playing after
  // unmount unless explicitly stopped, so kill everything here.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stopAll(), []);

  const playStep = (url: string) =>
    new Promise<void>((resolve) => {
      // Stop anything else in flight before starting this clip.
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      resolveRef.current?.();
      resolveRef.current = resolve;

      const a = new Audio(url);
      audioRef.current = a;
      const settle = () => {
        a.removeEventListener('ended', settle);
        a.removeEventListener('error', settle);
        if (resolveRef.current === resolve) {
          resolveRef.current = null;
          resolve();
        }
      };
      a.addEventListener('ended', settle);
      a.addEventListener('error', settle);
      void a.play();
    });

  /** Resolve a playable URL — prompts carry theirs; text and variable
   *  rows synthesize on demand (variable rows read their type's sample)
   *  and cache onto the row so repeat plays are free. */
  const resolveUrl = async (step: ResolvedStep, row: TtsRow): Promise<string | null> => {
    if (!step.playable) return null;
    if (step.url) return step.url;

    let text: string;
    let prefix: string;
    if (step.mode === 'variable') {
      if (!row.variableType) return null;
      text = variableSampleText(row.variableType);
      prefix = 'preview_var';
    } else if (step.mode === 'text') {
      if (!row.text || !row.text.trim()) return null;
      text = row.text;
      prefix = 'preview_step';
    } else {
      return null; // prompts always carry a url — nothing to synthesize
    }

    if (!language || !gender) {
      throw new Error('Pick a language and a voice first.');
    }
    setSynthesizingIndex(step.index);
    try {
      const convertReq: TtsConvertRequest = {
        userId: Number(userId),
        gender: gender as TtsConvertRequest['gender'],
        textScript: text,
        characterCount: text.length,
        language,
        filename: `${prefix}_${step.index + 1}_${Date.now()}`,
        category: 'tts',
      };
      const res = await api.post<TtsConvertResponse>(
        '/api/obd/convertion/texttospeech',
        convertReq,
      );
      if (res.status === 'fail') {
        throw new Error(res.msg || 'Synthesis failed.');
      }
      onPatchRow(step.index, {
        previewUrl: res.downloadUrl,
        previewTtsId: res.ttsId,
      });
      return res.downloadUrl;
    } finally {
      setSynthesizingIndex(null);
    }
  };

  const handlePlayStep = async (step: ResolvedStep) => {
    // Re-click on the playing step toggles off — decided from refs so
    // rapid clicks don't act on stale closure state.
    if (busyRef.current === 'single' && playingIdxRef.current === step.index) {
      stopAll();
      return;
    }
    if (busyRef.current) stopAll();

    const myRun = ++runIdRef.current;
    showBusy('single');
    showPlaying(step.index);
    try {
      const row = rows[step.index]!;
      const url = await resolveUrl(step, row);
      if (runIdRef.current !== myRun) return; // superseded — hands off
      if (!url) {
        toast.message('Nothing to play for this step.');
        return;
      }
      await playStep(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not play step.');
    } finally {
      if (runIdRef.current === myRun) {
        showBusy(null);
        showPlaying(null);
      }
    }
  };

  const handlePlayAll = async () => {
    if (busyRef.current === 'playAll') {
      stopAll();
      return;
    }
    if (busyRef.current) stopAll();
    if (steps.length === 0) return;

    const myRun = ++runIdRef.current;
    showBusy('playAll');
    try {
      for (const step of steps) {
        if (runIdRef.current !== myRun) return; // superseded — bail silently
        showPlaying(step.index);
        const row = rows[step.index]!;
        try {
          const url = await resolveUrl(step, row);
          if (runIdRef.current !== myRun) return;
          if (!url) continue; // variable without type / not picked — skip
          await playStep(url);
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Could not play step.');
          return;
        }
      }
    } finally {
      if (runIdRef.current === myRun) {
        showBusy(null);
        showPlaying(null);
        audioRef.current = null;
      }
    }
  };

  return {
    steps,
    playingIndex,
    busy,
    synthesizingIndex,
    handlePlayStep,
    handlePlayAll,
    stopAll,
  };
}
